import React from 'react';
import {useTranslation} from "react-i18next";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import {Link} from "react-router-dom";
import Button from "@material-ui/core/Button";
import {useFetchOnce} from "../hooks/useFetchOnce";
import {getSuppliers} from "../api";
import SeoComp from "./SeoComp";
import imageUrl from '../assets/background-image.jpg'
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Container from '@material-ui/core/Container';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import PaymentIcon from '@material-ui/icons/Payment';
import NotificationsIcon from '@material-ui/icons/Notifications';
import BusinessIcon from '@material-ui/icons/Business';
import PeopleIcon from '@material-ui/icons/People';
import SecurityIcon from '@material-ui/icons/Security';
import Footer from './Footer';

const useStyles = makeStyles((theme) => ({
    heroSection: {
        background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'white',
        padding: theme.spacing(8, 0),
        marginBottom: theme.spacing(4),
    },
    heroContent: {
        textAlign: 'center',
        padding: theme.spacing(3),
    },
    supplierCard: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[4],
        },
    },
    cardMedia: {
        height: 200,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    },
    cardContent: {
        flexGrow: 1,
    },
    ctaSection: {
        padding: theme.spacing(4, 0),
        textAlign: 'center',
        backgroundColor: theme.palette.grey[100],
    },
    buttonContainer: {
        marginTop: theme.spacing(2),
        '& > *': {
            margin: theme.spacing(1),
        },
    },
    featureSection: {
        padding: theme.spacing(8, 0),
        backgroundColor: theme.palette.background.paper,
    },
    featureCard: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: theme.spacing(3),
    },
    featureIcon: {
        fontSize: 48,
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.main,
    },
    benefitsSection: {
        padding: theme.spacing(8, 0),
        backgroundColor: theme.palette.grey[50],
    },
    benefitCard: {
        height: '100%',
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[1],
    },
    benefitIcon: {
        fontSize: 40,
        marginBottom: theme.spacing(2),
        color: theme.palette.secondary.main,
    },
    sectionTitle: {
        marginBottom: theme.spacing(6),
        textAlign: 'center',
    },
}));

const Homepage = () => {
    const {t} = useTranslation()
    const {suppliers} = useFetchOnce(getSuppliers) || {}
    const classes = useStyles();

    const features = [
        {
            icon: <AccessTimeIcon className={classes.featureIcon} />,
            title: "Reservas 24/7",
            description: "Reserva citas en cualquier momento y lugar - sin esperar horario comercial"
        },
        {
            icon: <CalendarTodayIcon className={classes.featureIcon} />,
            title: "Programación Inteligente",
            description: "Programación automatizada que evita reservas duplicadas y gestiona tu calendario"
        },
        {
            icon: <PaymentIcon className={classes.featureIcon} />,
            title: "Pagos Seguros",
            description: "Sistema de pago integrado para transacciones sin complicaciones"
        },
        {
            icon: <NotificationsIcon className={classes.featureIcon} />,
            title: "Recordatorios Automáticos",
            description: "Reduce las ausencias con recordatorios automáticos de citas"
        }
    ];

    const benefits = [
        {
            icon: <BusinessIcon className={classes.benefitIcon} />,
            title: "Enfócate en tu Negocio",
            description: "Dedica más tiempo a tus clientes y menos tiempo a gestionar citas"
        },
        {
            icon: <PeopleIcon className={classes.benefitIcon} />,
            title: "Amplía tu Base de Clientes",
            description: "Alcanza más clientes a través de nuestra plataforma y expande tu negocio"
        },
        {
            icon: <SecurityIcon className={classes.benefitIcon} />,
            title: "Gestión Profesional",
            description: "Deja que nuestro sistema maneje las tareas administrativas mientras te enfocas en tu experiencia"
        }
    ];

    return <Box>
        <SeoComp title={t('booking-system')} description={t('app-descp')} image={imageUrl}/>
        
        {/* Hero Section */}
        <Box className={classes.heroSection}>
            <Container maxWidth="md">
                <Box className={classes.heroContent}>
                    <Typography variant="h2" component="h1" gutterBottom>
                        {t('booking-system')}
                    </Typography>
                    <Typography variant="h5" component="h2" gutterBottom>
                        {t('app-descp')}
                    </Typography>
                    <Box className={classes.buttonContainer}>
                        <Button
                            component={Link}
                            to="/signup"
                            variant="contained"
                            color="primary"
                            size="large"
                        >
                            {t('signup')}
                        </Button>
                        <Button
                            component={Link}
                            to="/login"
                            variant="contained"
                            color="default"
                            size="large"
                        >
                            {t('login')}
                        </Button>
                    </Box>
                </Box>
            </Container>
        </Box>

        {/* Features Section */}
        <Box className={classes.featureSection}>
            <Container maxWidth="lg">
                <Typography variant="h3" component="h2" className={classes.sectionTitle}>
                    ¿Por qué elegir reservas en línea?
                </Typography>
                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} sm={6} md={3} spacing={4} key={index}>
                            <Card className={classes.featureCard}>
                                {feature.icon}
                                <Typography variant="h5" component="h3" gutterBottom>
                                    {feature.title}
                                </Typography>
                                <Typography variant="body1" color="textSecondary">
                                    {feature.description}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>

        {/* Benefits Section */}
        <Box className={classes.benefitsSection}>
            <Container maxWidth="lg">
                <Typography variant="h3" component="h2" className={classes.sectionTitle}>
                    Beneficios para Proveedores de Servicios
                </Typography>
                <Grid container spacing={4}>
                    {benefits.map((benefit, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Box className={classes.benefitCard}>
                                {benefit.icon}
                                <Typography variant="h5" component="h3" gutterBottom>
                                    {benefit.title}
                                </Typography>
                                <Typography variant="body1" color="textSecondary">
                                    {benefit.description}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>

        {/* Suppliers Grid */}
        <Container maxWidth="lg">
            <Grid container spacing={4}>
                {suppliers?.map(({images,professionId,fullName,shortHash})=>{
                    return <Grid item key={shortHash} xs={12} sm={6} md={4} lg={3}>
                        <Card 
                            component={'a'} 
                            href={`https://${shortHash}.agendia.mx`}
                            className={classes.supplierCard}
                            style={{ textDecoration: 'none' }}
                        >
                            <CardMedia
                                className={classes.cardMedia}
                                image={images[0]?.url || imageUrl}
                                title={`${professionId?.heName} - ${fullName}`}
                            />
                            <CardContent className={classes.cardContent}>
                                <Typography gutterBottom variant="h6" component="h2">
                                    {professionId?.heName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {fullName}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                })}
            </Grid>
        </Container>

        {/* CTA Section */}
        <Box className={classes.ctaSection}>
            <Container maxWidth="md">
                <Typography variant="h4" gutterBottom>
                    {t('booking-system-supplier')}
                </Typography>
                <Typography variant="h6" color="textSecondary" paragraph>
                    {t('app-descp-supplier')}
                </Typography>
                <Box className={classes.buttonContainer}>
                    <Button
                        component={Link}
                        to="/signup"
                        variant="contained"
                        color="primary"
                        size="large"
                    >
                        {t('signup')}
                    </Button>
                </Box>
            </Container>
        </Box>
        <Footer />
    </Box>
}

export default Homepage;